<template>
  <div class="card">
    <form @submit.prevent="submitForm" class="flex flex-col gap-3">
      <!-- Photo Upload -->
      <div v-if="isEditMode" class="flex items-center gap-2 pb-2 border-b border-gray-100">
        <div
          class="relative w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer group"
          @click="triggerPhotoInput"
        >
          <img v-if="photoPreview" :src="photoPreview" alt="Student photo" class="w-full h-full object-cover" />
          <i v-else class="pi pi-user text-xl text-gray-400"></i>
          <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <i class="pi pi-camera text-white text-sm"></i>
          </div>
        </div>
        <div>
          <p class="text-xs font-medium text-gray-700">Student Photo</p>
          <p class="text-[11px] text-gray-400">Click to upload (max 2MB, JPG/PNG)</p>
          <Button
            v-if="selectedPhotoFile"
            label="Upload"
            icon="pi pi-upload"
            size="small"
            class="mt-1 p-button-sm"
            :loading="isUploadingPhoto"
            @click.prevent="uploadPhoto"
          />
        </div>
        <input ref="photoInputRef" type="file" accept="image/*" class="hidden" @change="onPhotoSelected" />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">

        <!-- Surname -->
        <div class="flex flex-col gap-1.5">
          <label for="surname" class="text-sm font-semibold text-gray-700">Surname</label>
          <InputText id="surname" data-testid="input-student-surname" v-model="student.surname" placeholder="Enter Surname"
            :class="{ 'p-invalid': getErrorMessage('surname') }" />
          <small v-if="getErrorMessage('surname')" class="text-red-500">{{ getErrorMessage('surname') }}</small>
        </div>

        <!-- First Name -->
        <div class="flex flex-col gap-1.5">
          <label for="first_name" class="text-sm font-semibold text-gray-700">First Name</label>
          <InputText id="first_name" data-testid="input-student-first-name" v-model="student.first_name" placeholder="Enter First Name"
            :class="{ 'p-invalid': getErrorMessage('first_name') }" />
          <small v-if="getErrorMessage('first_name')" class="text-red-500">{{ getErrorMessage('first_name') }}</small>
        </div>

        <!-- Middle Name -->
        <div class="flex flex-col gap-1.5">
          <label for="other_name" class="text-sm font-semibold text-gray-700">Middle Name</label>
          <InputText id="other_name" v-model="student.other_name" placeholder="Enter Middle Name"
            :class="{ 'p-invalid': getErrorMessage('other_name') }" />
          <small v-if="getErrorMessage('other_name')" class="text-red-500">{{ getErrorMessage('other_name') }}</small>
        </div>

        <!-- Gender -->
        <div class="flex flex-col gap-1.5">
          <label for="gender" class="text-sm font-semibold text-gray-700">Gender</label>
          <Select id="gender" v-model="student.gender" :options="genderOptions" optionLabel="label"
            optionValue="value" placeholder="Select Gender" :class="{ 'p-invalid': getErrorMessage('gender') }" />
          <small v-if="getErrorMessage('gender')" class="text-red-500">{{ getErrorMessage('gender') }}</small>
        </div>

        <!-- Matric Number -->
        <div class="flex flex-col gap-1.5">
          <label for="matric_no" class="text-sm font-semibold text-gray-700">Matriculation Number</label>
          <InputText id="matric_no" v-model="student.matric_no" placeholder="Enter Matric Number"
            :class="{ 'p-invalid': getErrorMessage('matric_no') }" />
          <small v-if="getErrorMessage('matric_no')" class="text-red-500">{{ getErrorMessage('matric_no') }}</small>
        </div>

        <!-- Date of Birth -->
        <div class="flex flex-col gap-1.5">
          <label for="dob" class="text-sm font-semibold text-gray-700">Date of Birth</label>
          <Calendar id="dob" v-model="student.dob" showIcon dateFormat="yy-mm-dd" placeholder="YYYY-MM-DD"
            :class="{ 'p-invalid': getErrorMessage('dob') }" />
          <small v-if="getErrorMessage('dob')" class="text-red-500">{{ getErrorMessage('dob') }}</small>
        </div>

        <!-- Email -->
        <div class="flex flex-col gap-1.5">
          <label for="email" class="text-sm font-semibold text-gray-700">Email</label>
          <InputText id="email" v-model="student.email" placeholder="Enter Email"
            :class="{ 'p-invalid': getErrorMessage('email') }" />
          <small v-if="getErrorMessage('email')" class="text-red-500">{{ getErrorMessage('email') }}</small>
        </div>

        <!-- Phone -->
        <div class="flex flex-col gap-1.5">
          <label for="phone" class="text-sm font-semibold text-gray-700">Phone</label>
          <InputText id="phone" v-model="student.phone" placeholder="Enter Phone"
            :class="{ 'p-invalid': getErrorMessage('phone') }" />
          <small v-if="getErrorMessage('phone')" class="text-red-500">{{ getErrorMessage('phone') }}</small>
        </div>

        <!-- Classroom / Level -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-semibold text-gray-700">
            {{ isTertiary ? 'Level' : labels.CLASS }}
          </label>
          <Select v-model="classroomSectionId" :options="!isTertiary ? classroomSections : levelOptions"
            optionLabel="name" optionValue="id"
            :placeholder="loadingClassrooms ? 'Loading...' : 'Select ' + (isTertiary ? 'Level' : labels.CLASS)"
            :loading="loadingClassrooms"
            :class="{ 'p-invalid': getErrorMessage('classroom_section_id') }" filter />
          <small v-if="isEditMode" class="text-gray-500 text-xs">
            Changing this moves the student to the selected {{ isTertiary ? 'level' : labels.CLASS.toLowerCase() }}.
          </small>
          <small v-if="getErrorMessage('classroom_section_id')" class="text-red-500">{{
            getErrorMessage('classroom_section_id') }}</small>
        </div>

        <!-- Nationality and where the student lives. State of origin, LGA and a permanent
             home address are school custom fields (#574), shown under Additional information. -->
        <div class="col-span-full border-t border-surface-100 pt-2">
          <h3 class="text-sm font-semibold text-surface-700">Nationality and Address</h3>
        </div>

        <div class="flex flex-col gap-1.5">
          <label for="nationality" class="text-sm font-semibold text-surface-700">Nationality</label>
          <Select inputId="nationality" v-model="student.nationality" :options="nationalitySuggestions" editable
            placeholder="Select or type a nationality" data-testid="input-student-nationality"
            :invalid="!!getErrorMessage('nationality')" class="w-full" />
          <small v-if="getErrorMessage('nationality')" class="text-red-500">{{ getErrorMessage('nationality') }}</small>
        </div>

        <div class="flex flex-col gap-1.5 md:col-span-2 lg:col-span-1">
          <label for="home_address" class="text-sm font-semibold text-surface-700">Residential Address</label>
          <Textarea id="home_address" v-model="student.home_address" rows="2" autoResize
            placeholder="Where the student currently lives" data-testid="input-student-residential-address"
            :class="{ 'p-invalid': getErrorMessage('home_address') }" />
          <small v-if="getErrorMessage('home_address')" class="text-red-500">{{ getErrorMessage('home_address') }}</small>
        </div>

        <div class="flex flex-col gap-1.5">
          <label for="state" class="text-sm font-semibold text-surface-700">State of Residence</label>
          <InputText id="state" v-model="student.state" placeholder="Enter State of Residence"
            :class="{ 'p-invalid': getErrorMessage('state') }" />
          <small v-if="getErrorMessage('state')" class="text-red-500">{{ getErrorMessage('state') }}</small>
        </div>

        <div class="flex flex-col gap-1.5">
          <label for="city" class="text-sm font-semibold text-surface-700">City</label>
          <InputText id="city" v-model="student.city" placeholder="Enter City"
            :class="{ 'p-invalid': getErrorMessage('city') }" />
          <small v-if="getErrorMessage('city')" class="text-red-500">{{ getErrorMessage('city') }}</small>
        </div>

      </div>

      <!-- Additional information (#574): the school's student custom fields. Blank inputs when
           admitting; the student's stored values when editing, so the Edit dialog covers the whole record. -->
      <div v-if="customFieldDraft.entries.value.length" class="border-t border-surface-100 pt-2"
        data-testid="student-custom-fields">
        <h3 class="text-sm font-semibold text-surface-700 mb-2">Additional information</h3>
        <CustomFieldsFormSection v-model="customFieldDraft.model.value" :entries="customFieldDraft.entries.value"
          :errors="customFieldDraft.errors.value" idPrefix="student-cf" class="md:grid md:grid-cols-2 md:gap-x-4" />
      </div>

      <!-- Migration / Starting Balance (All Schools) -->
      <div v-if="isEditMode" class="col-span-full bg-yellow-50 p-2.5 rounded-md border border-yellow-200">
        <div class="flex justify-between items-center mb-1.5">
          <h3 class="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <i class="pi pi-exclamation-triangle text-yellow-600 text-xs"></i>
            Migration / Starting Balance Data
          </h3>
          <Button
            :label="isMigrationDataEditable ? 'Done' : 'Edit'"
            :icon="isMigrationDataEditable ? 'pi pi-check' : 'pi pi-pencil'"
            :severity="isMigrationDataEditable ? 'success' : 'warning'"
            text
            size="small"
            @click="isMigrationDataEditable = !isMigrationDataEditable"
            data-testid="btn-student-form-migration-toggle"
          />
        </div>
        <p class="text-xs text-gray-600 mb-2">
          <strong>Caution:</strong> These fields are for <em>migration purposes only</em>. If you are using the daily Attendance Module to mark registers, leave these as 0. Any value entered here will be added on top of the system's calculated total, effectively inflating the attendance count.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
           <div class="flex flex-col gap-1.5">
            <label for="initial_present_days" class="text-sm font-semibold text-gray-700">Initial Present Days</label>
            <InputNumber id="initial_present_days" v-model="student.initial_present_days" :min="0" placeholder="0" :disabled="!isMigrationDataEditable" />
          </div>
          <div class="flex flex-col gap-1.5">
            <label for="initial_absent_days" class="text-sm font-semibold text-gray-700">Initial Absent Days</label>
            <InputNumber id="initial_absent_days" v-model="student.initial_absent_days" :min="0" placeholder="0" :disabled="!isMigrationDataEditable" />
          </div>
          <div class="flex flex-col gap-1.5">
            <label for="initial_late_days" class="text-sm font-semibold text-gray-700">Initial Late Days</label>
            <InputNumber id="initial_late_days" v-model="student.initial_late_days" :min="0" placeholder="0" :disabled="!isMigrationDataEditable" />
          </div>
        </div>
      </div>

      <!-- Academic Configuration (All Schools) -->
       <div v-if="isEditMode" class="col-span-full border-t border-gray-100 pt-2">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Academic Configuration</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-semibold text-gray-700">Academic Calendar</label>
               <AcademicCalendarSelect v-model="academicCalendarId" placeholder="Select Academic Calendar" />
            </div>
             <div class="flex flex-col gap-1.5">
              <label for="status" class="text-sm font-semibold text-gray-700">Status</label>
              <InputText id="status" v-model="student.status" placeholder="Enter Status (e.g. Active, Suspended)" />
            </div>
          </div>
      </div>

       <!-- Tertiary Specific (Status, Units) -->
      <div v-if="isTertiary && isEditMode" class="col-span-full border-t border-gray-100 pt-2">
         <h3 class="text-sm font-semibold text-gray-700 mb-2">Tertiary Details</h3>
         <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
             <div class="flex flex-col gap-1.5">
              <label for="min_units" class="text-sm font-semibold text-gray-700">Min Units Per Registration</label>
              <InputNumber id="min_units" v-model="student.min_units_per_registration" :min="0" placeholder="No minimum" />
              <small class="text-xs text-gray-400">
                Leave empty for no minimum. When set, course registrations below this total are rejected —
                avoid setting it for modular/residence students who register one module at a time.
              </small>
            </div>
            <div class="flex flex-col gap-1.5">
              <label for="max_units" class="text-sm font-semibold text-gray-700">Max Units Per Registration</label>
              <InputNumber id="max_units" v-model="student.max_units_per_registration" :min="0" placeholder="No limit" />
              <small class="text-xs text-gray-400">
                Leave empty for no limit. When set, registrations totalling more than this are rejected.
              </small>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-semibold text-gray-700">Entry Level</label>
              <InputText v-model="student.entry_level" placeholder="e.g. 200 for direct entry" />
              <small class="text-xs text-gray-400">Leave empty if student starts from 100 level</small>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-semibold text-gray-700">Min Graduation Units</label>
              <InputNumber v-model="student.min_graduation_units" :min="0" placeholder="Override programme default" />
              <small class="text-xs text-gray-400">Leave empty to use programme default</small>
            </div>
         </div>
      </div>

      <!-- Programmes (Tertiary Only) -->
      <div class="flex flex-col gap-1.5" v-if="isTertiary">
        <label class="text-sm font-semibold text-gray-700">Programme</label>
        <Select v-model="programmeId" :options="programmeOptions"
              optionLabel="label"
              optionValue="id"
          placeholder="Select Programme" :filter="true" :class="{ 'p-invalid': getErrorMessage('programme_id') }" />
        <small v-if="getErrorMessage('programme_id')" class="text-red-500">{{ getErrorMessage('programme_id') }}</small>
      </div>

      <!-- Campus (Tertiary Only) -->
      <div class="flex flex-col gap-1.5" v-if="isTertiary && campuses.length > 0">
        <label class="text-sm font-semibold text-gray-700">Campus</label>
        <Select v-model="campusId" :options="campuses" optionLabel="name" optionValue="id" placeholder="Select Campus"
          :filter="true" />
      </div>

      <div class="flex justify-end mt-2">
        <Button type="submit" :label="isEditMode ? 'Update Student' : 'Upload Student Details'" icon="pi pi-check"
          :loading="isSubmitting" class="p-button-sm w-full md:w-auto" data-testid="btn-student-form-submit" />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import dayjs from 'dayjs';
import { useMutation } from '@vue/apollo-composable';
import { useUserStore } from '@/store/userStore';
import { useStudentMutations } from '@/composables/mutations/students/useStudentMutations';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useStudents } from '@/composables/queries/useStudent';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useCampuses } from '@/composables/queries/useCampuses';
import { useToast } from 'primevue/usetoast';
import type { StudentInput, EditStudentInput, ProgrammeType } from '@/types/composition-functions';
import { UPLOAD_STUDENT_PHOTO } from '@/graphql';

import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { getClassroomSectionLabel } from '@/utils/classroomLabel';
import { useFormErrors } from '@/composables/useFormErrors';
import CustomFieldsFormSection from '@/components/common/CustomFieldsFormSection.vue';
import { useCustomFieldDraft } from '@/composables/forms/useCustomFieldDraft';
import { CustomFieldEntity } from '@/types/composition-functions';
import { NATIONALITY_SUGGESTIONS, pickOriginFields } from '@/utils/studentOrigin';

const { labels } = useSchoolLabels();
const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

// Define extended state for the form to include new fields not present in base StudentInput
interface ExtendedStudentFormState extends StudentInput {
  initial_present_days?: number | null;
  initial_absent_days?: number | null;
  initial_late_days?: number | null;
  status?: string;
  min_units_per_registration?: number | null;
  max_units_per_registration?: number | null;
  entry_level?: string | null;
  min_graduation_units?: number | null;
}

const props = defineProps<{
  isEditMode?: boolean;
  initialData?: Record<string, unknown>;
}>();

const emit = defineEmits(['saved']);

const nationalitySuggestions = [...NATIONALITY_SUGGESTIONS];
// The school's student custom fields (#574): blank when admitting; when editing,
// recordId makes the draft load the student's stored values and send only changes.
const customFieldDraft = useCustomFieldDraft(CustomFieldEntity.Student, {
  recordId: computed(() => (props.isEditMode ? (props.initialData?.id as string | number | undefined) ?? null : null)),
});

const toast = useToast();
const classroomSectionId = ref<string | null>('');
const programmeId = ref<number | null>(null);
const campusId = ref<number | null>(null);
const loadingClassrooms = ref(true);
const isSubmitting = ref(false);

const { refetchStudents } = useStudents({ getStudentsOptions: { autoFetch: false } });
const { classrooms, school, refetchSchool, loadingSchool } = useSchoolQuery();
const { programmes, loadSchoolProgramme } = useProgrammes({ autoFetch: true, first: 200 });
const { campuses, loadCampuses } = useCampuses({ autoFetch: true });

const { isTertiary, schoolType } = useSchoolFeatures();
const academicCalendarId = ref<string | undefined>(undefined);
const isMigrationDataEditable = ref(false);

// Photo upload
const photoInputRef = ref<HTMLInputElement | null>(null);
const photoPreview = ref<string | null>(null);
const selectedPhotoFile = ref<File | null>(null);
const isUploadingPhoto = ref(false);
const { mutate: uploadStudentPhotoMutation } = useMutation(UPLOAD_STUDENT_PHOTO);

function triggerPhotoInput() {
  photoInputRef.value?.click();
}

function onPhotoSelected(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) {
    toast.add({ severity: 'error', summary: 'File too large', detail: 'Photo must be under 2MB.', life: 3000 });
    return;
  }
  selectedPhotoFile.value = file;
  photoPreview.value = URL.createObjectURL(file);
}

async function uploadPhoto() {
  if (!selectedPhotoFile.value || !props.initialData?.id) return;
  try {
    isUploadingPhoto.value = true;
    const result = await uploadStudentPhotoMutation({
      student_id: props.initialData.id,
      photo: selectedPhotoFile.value,
    });
    const newPhoto = (result?.data as Record<string, unknown> as { uploadStudentPhoto?: { photo?: string } })?.uploadStudentPhoto?.photo;
    if (newPhoto) {
      photoPreview.value = newPhoto;
    }
    selectedPhotoFile.value = null;
    toast.add({ severity: 'success', summary: 'Photo uploaded', detail: 'Student photo updated.', life: 3000 });
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Could not upload photo.';
    toast.add({ severity: 'error', summary: 'Upload failed', detail: message, life: 4000 });
  } finally {
    isUploadingPhoto.value = false;
  }
}

// Programme Options
const programmeOptions = computed(() =>
  programmes.value?.map((p: ProgrammeType) => ({
    label: `${p.program_name}${p.degree ? ` — ${p.degree}` : ''}${p.type ? ` — ${p.type}` : ''}`,
    id: p.id,
  })) || [],
);

const genderOptions = [
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
];

const levelOptions = computed(() =>
  (classrooms.value || []).map((classroom) => ({
    id: classroom?.classroomSections?.[0]?.id || '',
    name: classroom?.name || '',
  })),
);

const classroomSections = computed(() => {
  const sections: { id: string; name: string }[] = [];

  if (classrooms.value) {
    classrooms.value.forEach((classroom) => {
      if (classroom?.classroomSections?.length) {
        classroom.classroomSections.forEach((cs) => {
          if (cs) {
            sections.push({
              id: cs.id,
              name: getClassroomSectionLabel(cs, classroom, schoolType.value),
            });
          }
        });
      }
    });
  }

  const unique = new Map();
  sections.forEach((s) => unique.set(s.id, s));
  return Array.from(unique.values());
});

const student = ref<ExtendedStudentFormState>({
  surname: '',
  first_name: '',
  other_name: '',
  dob: null,
  gender: '',
  matric_no: '',
  email: '',
  phone: '',
  home_address: '',
  state: '',
  city: '',
  nationality: '',
  // New Fields
  //initial_present_days: null,
  // initial_absent_days: null,
  // initial_late_days: null,
  // status: "",
  // min_units_per_registration: null,
  // max_units_per_registration: null,
});

const userStore = useUserStore();
const { createStudent, editStudent, creatingStudent, editingStudent } = useStudentMutations();

watch(
  () => props.initialData,
  (newData) => {
    if (!newData || !props.isEditMode) return;

    // student_academic_calendar is loosely typed on initialData (Record<string, unknown>);
    // narrow it once so the nested field reads below are type-safe.
    const academic = (newData.student_academic_calendar ?? {}) as {
      initial_present_days?: number | null;
      initial_absent_days?: number | null;
      initial_late_days?: number | null;
      status?: string | null;
      min_units_per_registration?: number | null;
      max_units_per_registration?: number | null;
      entry_level?: string | null;
      min_graduation_units?: number | null;
      academic_calendar_id?: string | null;
    };

    const origin = pickOriginFields(newData);

    // profile fields
    student.value = {
      surname: newData.surname || '',
      first_name: newData.first_name || '',
      other_name: newData.other_name || '',
      dob: newData.dob ? new Date(newData.dob) : null,
      gender: newData.gender
        ? newData.gender.charAt(0).toUpperCase() + newData.gender.slice(1)
        : '',
      matric_no: newData.matric_no || '',
      email: newData.email || '',
      phone: newData.phone || '',
      ...origin,

      // New fields from nested object
      initial_present_days: academic.initial_present_days ?? null,
      initial_absent_days: academic.initial_absent_days ?? null,
      initial_late_days: academic.initial_late_days ?? null,
      status: academic.status || '',
      min_units_per_registration: academic.min_units_per_registration ?? null,
      max_units_per_registration: academic.max_units_per_registration ?? null,
      entry_level: academic.entry_level ?? null,
      min_graduation_units: academic.min_graduation_units ?? null,
    };

    // Photo
    photoPreview.value = newData.photo || null;

    // dropdown refs
    classroomSectionId.value = newData.classroom_section_id || null;
    programmeId.value = newData.programme_id || null;
    campusId.value = newData.campus_id || null;
    academicCalendarId.value = academic.academic_calendar_id || undefined;
  },
  { immediate: true },
);

// The backend types these as Int and its `integer` rule rejects a present null, so
// coerce to a whole number when there is one, otherwise return undefined to OMIT the
// field from the payload (loosely-typed initialData can also carry numeric strings).
const toIntOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Math.trunc(Number(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const submitForm = async() => {
  clearErrors(); // Clear previous server errors

  if (!customFieldDraft.validate()) {
    toast.add({ severity: 'error', summary: 'Validation Error', detail: 'Fill in the required additional information.', life: 3000 });
    return;
  }

  try {
    isSubmitting.value = true;
    const formattedDob = student.value.dob
      ? dayjs(student.value.dob).format('YYYY-MM-DD')
      : null;

    const studentData = {
      ...student.value,
      dob: formattedDob,
      gender: student.value.gender?.toLowerCase() || undefined,
    };

    if (props.isEditMode && props.initialData?.id) {
      // Edit Mode
      const editInput: EditStudentInput = {
        student_id: props.initialData.id,
        profile: {
          surname: studentData.surname,
          first_name: studentData.first_name,
          other_name: studentData.other_name,
          matric_no: studentData.matric_no,
          dob: studentData.dob || undefined,
          gender: studentData.gender,
          email: studentData.email,
          phone: studentData.phone,
          home_address: studentData.home_address,
          state: studentData.state,
          city: studentData.city,
          nationality: studentData.nationality,
        },
        classroom_section_id: classroomSectionId.value, // Allow updating classroom ID directly too
        campus_id: campusId.value ? Number(campusId.value) : null,
        student_academic_calendar: {
          academic_calendar_id: academicCalendarId.value,
          classroom_section_id: classroomSectionId.value, // Also update inside pivot if needed
          initial_absent_days: toIntOrUndefined(studentData.initial_absent_days),
          initial_late_days: toIntOrUndefined(studentData.initial_late_days),
          initial_present_days: toIntOrUndefined(studentData.initial_present_days),
          min_units_per_registration: toIntOrUndefined(studentData.min_units_per_registration),
          max_units_per_registration: toIntOrUndefined(studentData.max_units_per_registration),
          entry_level: studentData.entry_level || undefined,
          min_graduation_units: toIntOrUndefined(studentData.min_graduation_units),
          programme_id: programmeId.value ? String(programmeId.value) : undefined,
          status: studentData.status,
        },
      };

      await editStudent(editInput);

      // Saved second, as on admission: the profile changes are already in, so a
      // failure here is reported as a warning rather than as a failed edit.
      const failure = await customFieldDraft.saveFor(props.initialData.id as string | number);
      if (failure) {
        toast.add({
          severity: 'warn',
          summary: 'Student updated — additional information not saved',
          detail: `${failure} Open the student's profile to fill it in.`,
          life: 8000,
        });
      }
    } else {
      // Create Mode
      const createStudentInput = {
        student: studentData,
        classroom_section_id: classroomSectionId.value,
        programme_id: programmeId.value ? Number(programmeId.value) : null,
        campus_id: campusId.value ? Number(campusId.value) : null,
      };

      const created = await createStudent(createStudentInput);

      // The values need the new student's id, so they are saved second. A
      // failure here must not look like the admission failed: the student
      // exists, so say so and point to the profile.
      if (created?.id) {
        const failure = await customFieldDraft.saveFor(created.id);
        if (failure) {
          toast.add({
            severity: 'warn',
            summary: 'Student created — additional information not saved',
            detail: `${failure} Open the student's profile to fill it in.`,
            life: 8000,
          });
        }
      }
    }

    // Refresh and cleanup
    await refetchStudents({ page: 1, first: 10 });
    if (!props.isEditMode) {
      resetForm();
    }
    emit('saved'); // Emit event to notify parent (e.g. to close dialog)

  } catch (err: unknown) {
    handleServerErrors(err); // Handle server errors
    if (Object.keys(serverErrors.value).length === 0) {
      const message = err instanceof Error ? err.message : 'Failed to save student details.';
      toast.add({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: 'Validation Error', detail: 'Please check the form for errors.', life: 3000 });
    }
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  clearErrors();
  student.value = {
    surname: '',
    first_name: '',
    other_name: '',
    dob: null,
    gender: '',
    matric_no: '',
    email: '',
    phone: '',
    home_address: '',
    state: '',
    city: '',
    nationality: '',
  };
  customFieldDraft.reset();
  classroomSectionId.value = '';
  programmeId.value = null;
  campusId.value = null;
};

onMounted(async() => {
  loadingClassrooms.value = true;
  await refetchSchool({ fetchPolicy: 'network-only' });
  loadingClassrooms.value = false;
});

watch(loadingSchool, (val) => {
  loadingClassrooms.value = val;
});
</script>
